/**
 * ComponentService - Handles component processing and merging
 * Centralizes the complex logic for merging vehicle features with UI configurations
 */

import { APIService } from './APIService.js';
import { ActionTypes } from '../utils/ActionSystem';

/**
 * Cache for vehicle features to avoid repeated API calls
 */
let vehicleFeaturesCache = null;

/**
 * Get vehicle features (cached)
 */
async function getVehicleFeatures() {
  if (!vehicleFeaturesCache) {
    console.log('🔄 Loading vehicle features for component merging...');
    const data = await APIService.fetchVehicleFeatures();
    vehicleFeaturesCache = data.vehicleHMIFeatures;
  }
  return vehicleFeaturesCache;
}

/**
 * Clear the vehicle features cache (useful for testing or updates)
 */
export function clearVehicleFeaturesCache() {
  vehicleFeaturesCache = null;
}

/**
 * Merge vehicle feature data with a configuration component
 * This is the core logic extracted from UIBuilder
 * 
 * @param {Object} configComponent - Component from UI configuration
 * @returns {Object} - Merged component with vehicle feature data
 */
export async function mergeVehicleFeatureWithConfig(configComponent) {
  console.log('🔧 ComponentService: mergeVehicleFeatureWithConfig called with:', configComponent);
  
  try {
    const vehicleFeatures = await getVehicleFeatures();
    
    // Check if this component has vehicle feature behavior
    const behavior = configComponent.behavior;
    console.log('🔧 ComponentService: behavior check:', behavior);
    
    if (behavior?.action === ActionTypes.VehicleFunction && behavior.category && behavior.functionName) {
      console.log('🔧 ComponentService: Vehicle feature detected, looking up:', behavior.category, behavior.functionName);
      
      // Find the matching vehicle feature
      const category = vehicleFeatures[behavior.category];
      const feature = category?.features[behavior.functionName];
      
      if (feature) {
        console.log(`🔄 ComponentService: Merging vehicle feature: ${behavior.category}.${behavior.functionName}`);
        
        // Create merged component: behavior from vehicle feature + visuals from config
        console.log('🔍 ComponentService BEFORE merge - configComponent.content:', configComponent.content);
        console.log('🔍 iconOffsetX from config:', configComponent.content?.iconOffsetX);
        console.log('🔍 iconOffsetY from config:', configComponent.content?.iconOffsetY);
        
        const mergedComponent = {
          ...configComponent,
          // Set component type based on vehicle feature
          type: 'button', // All vehicle features are buttons for now
          // Keep visual properties from config
          position: configComponent.position,
          size: configComponent.size,
          style: configComponent.style,
          content: {
            // Merge content: use config visuals but preserve vehicle feature names/icons
            primaryText: configComponent.content?.primaryText || feature.name,
            icon: configComponent.content?.icon || feature.icon,
            iconType: configComponent.content?.iconType, // Preserve icon type (icon vs image)
            iconImage: configComponent.content?.iconImage, // Preserve icon image data
            iconImageName: configComponent.content?.iconImageName, // Preserve icon image filename
            iconWidth: configComponent.content?.iconWidth, // Preserve icon width
            iconHeight: configComponent.content?.iconHeight, // Preserve icon height
            iconPosition: configComponent.content?.iconPosition, // Preserve icon position
            iconFit: configComponent.content?.iconFit, // Preserve icon fit style
            iconOffsetX: configComponent.content?.iconOffsetX ?? 0, // Preserve icon X offset (use 0 if undefined)
            iconOffsetY: configComponent.content?.iconOffsetY ?? 0, // Preserve icon Y offset (use 0 if undefined)
            secondaryText: configComponent.content?.secondaryText || ''
          },
          accessibility: {
            ...configComponent.accessibility,
            ariaLabel: `${feature.name} control`,
            voiceCommand: behavior.functionName
          },
          // Replace behavior with current vehicle feature definition
          behavior: {
            action: ActionTypes.VehicleFunction,
            endpoint: feature.endpoint,
            method: 'click',
            category: behavior.category,
            functionName: behavior.functionName,
            behaviorType: feature.type
          },
          // Replace toggle state with current vehicle feature definition
          toggleState: (feature.type === 'toggle' && feature.states)
          ? {
              isToggleable: true,
              currentState: configComponent.toggleState?.currentState || feature.defaultState || 'off',
              states: feature.states
            }
          : (configComponent.toggleState
              ? configComponent.toggleState
              : {
                  isToggleable: false,
                  currentState: 'off',
                  states: null
                })
        };
        
        console.log('🔧 ComponentService: Merged component result:', {
          id: mergedComponent.id,
          type: mergedComponent.type,
          behaviorType: mergedComponent.behavior?.behaviorType,
          isToggleable: mergedComponent.toggleState?.isToggleable,
          currentState: mergedComponent.toggleState?.currentState,
          hasStates: !!mergedComponent.toggleState?.states
        });
        
        return mergedComponent;
      } else {
        console.warn(`⚠️ ComponentService: Vehicle feature not found: ${behavior.category}.${behavior.functionName}`);
      }
    }
    
    // Return component as-is if no vehicle feature merge needed
    return configComponent;
  } catch (error) {
    console.error('ComponentService: Failed to merge vehicle feature:', error);
    return configComponent;
  }
}

/**
 * Merge vehicle features with an array of components
 * 
 * @param {Array} components - Array of components to merge
 * @returns {Promise<Array>} - Array of merged components
 */
export async function mergeVehicleFeaturesWithComponents(components) {
  console.log('🔧 ComponentService: Merging vehicle features with', components.length, 'components...');
  
  const mergedComponents = await Promise.all(
    components.map(async (comp) => {
      const mergedComponent = await mergeVehicleFeatureWithConfig(comp);
      return mergedComponent;
    })
  );
  
  console.log('🔧 ComponentService: Component merging complete. Original:', components.length, 'Merged:', mergedComponents.length);
  return mergedComponents;
}

/**
 * Scale a component for a given viewport scale
 * 
 * @param {Object} component - Component to scale
 * @param {number} scale - Scale factor
 * @returns {Object} - Scaled component
 */
export function scaleComponentForViewport(component, scale) {
  return {
    ...component, // Preserve ALL component properties
    position: {
      x: component.position.x * scale,
      y: component.position.y * scale
    },
    size: {
      width: component.size.width * scale,
      height: component.size.height * scale
    }
  };
}

/**
 * Update toggle state for a component
 * 
 * @param {Object} component - Component to update
 * @param {string} newState - New toggle state
 * @returns {Object} - Updated component
 */
export function updateComponentToggleState(component, newState) {
  if (!component.toggleState?.isToggleable) {
    console.warn('Attempting to update toggle state on non-toggleable component:', component.id);
    return component;
  }
  
  return {
    ...component,
    toggleState: {
      ...component.toggleState,
      currentState: newState
    }
  };
}

/**
 * ComponentService - Main service object with all component-related functions
 */
export const ComponentService = {
  mergeVehicleFeatureWithConfig,
  scaleComponentForViewport,
  updateComponentToggleState
};